import { motion } from "framer-motion";
import { Zap, Wrench, Armchair, ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const IMGS = {
  electrical:
    "https://images.unsplash.com/photo-1544724569-5f546fd6f2b5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHxfHxjb21tZXJjaWFscyUyMGVsZWN0cmljYWwlMjBpbnN0YWxsYXRpb24mZW58MHx8fDE3OTEwMjY5NTh8MA&ixlib=rb-4.1.0&q=85",
  plumbing:
    "https://customer-assets-lqy194kg.emergentagent.net/job_construction-hub-734/artifacts/e7lz3pl4_i6.jpeg",
  furniture:
    "https://customer-assets-lqy194kg.emergentagent.net/job_construction-hub-734/artifacts/yvxh0tp0_i1.jpeg",
};

const CardShell = ({ testid, children, className = "", delay = 0 }) => (
  <motion.div
    data-testid={testid}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
    className={`group relative overflow-hidden border border-[#2E333D] bg-[#1A1D24] transition-colors duration-300 hover:border-[#FFFFFF]/60 ${className}`}
  >
    {children}
  </motion.div>
);

const ScopeList = ({ items }) => (
  <ul className="space-y-2">
    {items.map((s) => (
      <li key={s} className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.15em] text-[#9CA3AF]">
        <span className="h-1.5 w-1.5 rotate-45 bg-[#FFFFFF]" aria-hidden="true" /> {s}
      </li>
    ))}
  </ul>
);

export const Services = () => (
  <section id="services" className="scroll-mt-24 bg-[#14171D] py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="mb-14 flex flex-wrap items-end justify-between gap-6">
        <div>
          <Eyebrow>What we do</Eyebrow>
          <h2 className="mt-4 font-display text-4xl font-extrabold uppercase tracking-tight text-[#F3F4F6] sm:text-5xl">
            Three disciplines.
            <br />
            One standard.
          </h2>
        </div>
        <p className="max-w-sm text-sm leading-relaxed text-[#9CA3AF]">
          A single accountable partner for the systems that make a building work — and the furniture
          that makes it yours.
        </p>
      </Reveal>

      <div className="grid gap-px bg-[#2E333D] md:grid-cols-3">
        <CardShell testid="service-card-electrical" className="md:col-span-2 md:row-span-2" delay={0}>
          <img
            src={IMGS.electrical}
            alt="Industrial electrical switchgear panel and circuit breaker assembly"
            className="h-64 w-full object-cover opacity-80 transition-all duration-500 group-hover:scale-[1.03] group-hover:opacity-100 md:h-80"
            loading="lazy"
          />
          <div className="p-8">
            <div className="mb-4 flex items-center gap-3">
              <Zap size={22} className="text-[#FFFFFF]" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#9CA3AF]">Discipline 01</span>
            </div>
            <h3 className="font-display text-3xl font-bold uppercase tracking-tight text-[#F3F4F6]">
              Electrical Engineering
            </h3>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-[#9CA3AF]">
              High-voltage switchgear, commercial wiring, panel fabrication and hospital-grade power
              systems — the discipline behind RIMS Hospital, Manipur.
            </p>
            <div className="mt-6">
              <ScopeList items={["HV Switchgear & Panels", "Commercial Wiring", "Hospital Power Systems", "AMC & Maintenance"]} />
            </div>
          </div>
        </CardShell>

        <CardShell testid="service-card-plumbing" delay={0.1}>
          <img
            src={IMGS.plumbing}
            alt="Ceiling pipe runs installed by Gativan in a residential tower"
            className="h-44 w-full object-cover opacity-80 transition-all duration-500 group-hover:scale-[1.03] group-hover:opacity-100"
            loading="lazy"
          />
          <div className="p-8">
            <div className="mb-4 flex items-center gap-3">
              <Wrench size={20} className="text-[#FFFFFF]" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#9CA3AF]">Discipline 02</span>
            </div>
            <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-[#F3F4F6]">
              Industrial Plumbing
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#9CA3AF]">
              Fluid mechanical systems, commercial piping, valves and fire-fighting lines.
            </p>
          </div>
        </CardShell>

        <CardShell testid="service-card-furniture" delay={0.2}>
          <img
            src={IMGS.furniture}
            alt="Living room with custom sofa and ceiling lighting by Gativan"
            className="h-44 w-full object-cover opacity-80 transition-all duration-500 group-hover:scale-[1.03] group-hover:opacity-100"
            loading="lazy"
          />
          <div className="p-8">
            <div className="mb-4 flex items-center gap-3">
              <Armchair size={20} className="text-[#FFFFFF]" />
              <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#9CA3AF]">Discipline 03</span>
            </div>
            <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-[#F3F4F6]">
              Bespoke Furniture
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-[#9CA3AF]">
              Custom commercial furniture and interior joinery, built for heavy daily use.
            </p>
          </div>
        </CardShell>

        <a
          href="#contact"
          data-testid="services-cta-card"
          className="group relative flex items-center justify-between gap-4 overflow-hidden bg-[#FFFFFF] p-8 md:col-span-1"
        >
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-black/70">Turnkey fit-out?</p>
            <p className="mt-2 font-display text-2xl font-black uppercase leading-tight text-black">
              One call.
              <br />
              All three.
            </p>
          </div>
          <ArrowUpRight size={32} className="shrink-0 text-black transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </a>
      </div>
    </div>
  </section>
);
